import { DirectionDto } from 'api/generated';
import { routes } from 'constant/routes';
import { useAsyncAction, useThemeMode } from 'hooks';
import { FC, ReactNode, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { actions, selectors, useAppSelector } from 'store';
import styled, { ThemeProvider } from 'styled-components';

type DirectionThemeProviderProps = {
  children: ReactNode;
};

const DirectionThemeProvider: FC<DirectionThemeProviderProps> = ({ children }) => {
  const [getDirectionByUrl, isLoading] = useAsyncAction(actions.direction.getDirectionByUrl);
  const direction = useAppSelector(selectors.direction.selectDirection);
  const [directionInfo, setDirectionInfo] = useState<DirectionDto | null>(null);
  const [hasFinishedDirectionRequest, setHasFinishedDirectionRequest] = useState(false);
  const theme = useThemeMode(direction);
  const pathname = window.location.pathname;
  const siteUrl = window.location.origin;
  const isIframePage = pathname.includes(routes.createUser);

  const shouldShowSpinner = !isIframePage && !direction && (isLoading || !hasFinishedDirectionRequest);

  const getCurrentDirection = async (url: string) => {
    await getDirectionByUrl({ url });
  };

  useEffect(() => {
    setDirectionInfo(direction);
  }, [direction]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        await getCurrentDirection(siteUrl);
      } catch {
        // If direction lookup fails (e.g. network error / 404), don't crash the app via an
        // unhandled rejection; UI falls back to "Not found".
      } finally {
        if (isMounted) setHasFinishedDirectionRequest(true);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [siteUrl]);

  if (shouldShowSpinner) {
    return (
      <Root $withBackground={!isIframePage}>
        <Spinner aria-label="Loading" role="status" />
      </Root>
    );
  }

  if (isIframePage || direction) {
    return (
      <>
        <Helmet>
          <meta property="og:title" content={directionInfo?.linkTitle || 'LMS'} />
          <meta
            property="og:description"
            content={directionInfo?.linkDescription || 'Web site created using create-react-app'}
          />
          <meta
            name="description"
            content={directionInfo?.linkDescription || 'Web site created using create-react-app'}
          />
          <link rel="icon" href={directionInfo?.faviconUrl} type="image/x-icon" />
          <link rel="shortcut icon" href={directionInfo?.faviconUrl} type="image/x-icon" />
          <link rel="apple-touch-icon" href={directionInfo?.faviconUrl} sizes="180x180" />
          <link rel="icon" href={directionInfo?.faviconUrl} type="image/png" sizes="32x32" />
          <link rel="icon" href={directionInfo?.faviconUrl} type="image/png" sizes="16x16" />
          <link rel="icon" href={directionInfo?.faviconUrl} type="image/png" sizes="192x192" />
          <link rel="icon" href={directionInfo?.faviconUrl} type="image/png" sizes="512x512" />
          <title>{directionInfo?.linkTitle || 'LMS'}</title>
        </Helmet>

        {/* @ts-ignore - ThemeProvider type compatibility issue with React 18 */}
        <ThemeProvider theme={theme.default}>{children}</ThemeProvider>
      </>
    );
  }

  return (
    <Root $withBackground={!isIframePage}>
      <>Not found</>
    </Root>
  );
};

export default DirectionThemeProvider;

const Root = styled.div<{ $withBackground: boolean }>`
  width: 100vw;
  height: 100vh;
  position: absolute;
  z-index: 1000;

  display: flex;
  justify-content: center;
  align-items: center;

  ${({ $withBackground }) => $withBackground && 'background-color: white;'};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 4px solid rgba(0, 0, 0, 0.12);
  border-top-color: rgba(0, 0, 0, 0.5);
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
